import type {
  ResearchRoomAnalysis,
  ResearchRoomMessage,
} from '@repo/contracts';

/**
 * What the write-up card draws that code can work out on its own — free,
 * and fresh every round, while the words wait for a milestone.
 */

/** One persona's answer in one round, as a mood: +1 positive, 0 neutral,
 *  -1 negative. Rounds they did not answer, or polls, are gaps. */
export interface MoodPoint {
  round: number;
  value: -1 | 0 | 1;
  messageId: string;
}

const MOOD = { Negative: -1, Neutral: 0, Positive: 1 } as const;

/** Every round anyone answered in, in order — the mood line's x axis. */
export function answeredRounds(messages: ResearchRoomMessage[]): number[] {
  return [
    ...new Set(
      messages
        .filter((m) => m.kind === 'answer' && m.status === 'done')
        .map((m) => m.round),
    ),
  ].sort((a, b) => a - b);
}

/** How each persona's answers read, round by round. The last answer in a
 *  round speaks for it — a follow-up is what they think now. */
export function moodSeries(
  messages: ResearchRoomMessage[],
  personaIds: string[],
): Map<string, MoodPoint[]> {
  const series = new Map<string, MoodPoint[]>(personaIds.map((id) => [id, []]));
  for (const message of messages) {
    if (message.kind !== 'answer' || !message.sentiment || !message.speakerId) {
      continue;
    }
    const points = series.get(message.speakerId);
    if (!points) continue;
    const point: MoodPoint = {
      messageId: message.id,
      round: message.round,
      value: MOOD[message.sentiment],
    };
    const last = points.at(-1);
    if (last?.round === message.round) points[points.length - 1] = point;
    else points.push(point);
  }
  return series;
}

/** Rounds Floto surfaced a milestone on — marks on the mood line. */
export function milestoneRounds(messages: ResearchRoomMessage[]): number[] {
  return messages
    .filter((m) => m.kind === 'summary' && m.milestone)
    .map((m) => m.round);
}

/** Whether the room reached a milestone the write-up has not covered —
 *  what writes it up on its own after a round. */
export function milestonePending(
  messages: ResearchRoomMessage[],
  analysis: ResearchRoomAnalysis | undefined,
): boolean {
  const latest = Math.max(-1, ...milestoneRounds(messages));
  return latest >= 0 && latest > (analysis?.round ?? -1);
}

/** Rounds answered before the write-up tab offers one, when no milestone
 *  has written it up yet: enough said for a write-up to be worth reading. */
export const OFFER_AFTER_ROUNDS = 3;

/**
 * Whether the room's write-up tab shows at all: once there is a write-up,
 * or once the room has said enough to write one up by hand. Before that it
 * would only be offering a write-up of nothing.
 */
export function writeUpShown(
  messages: ResearchRoomMessage[],
  analysis: ResearchRoomAnalysis | undefined,
): boolean {
  if (analysis) return true;
  const rounds = new Set(
    messages
      .filter(
        (m) =>
          (m.kind === 'answer' || m.kind === 'poll') && m.status === 'done',
      )
      .map((m) => m.round),
  );
  return rounds.size >= OFFER_AFTER_ROUNDS;
}

/** Themes not in the write-up the researcher last looked at. Nothing is new
 *  the first time they look: everything is. */
export function newThemeIds(
  analysis: ResearchRoomAnalysis,
  seen: { themeIds: string[] } | undefined,
): Set<string> {
  if (!seen) return new Set();
  const before = new Set(seen.themeIds);
  return new Set(
    analysis.themes.map((t) => t.id).filter((id) => !before.has(id)),
  );
}
